import { createSelector } from 'reselect';

import { RootModel } from '../models';
import { FloorTypes, ProjectChildItemTypes, ProjectItemTypes, StoreImageTypes } from '../../types';
import { PROJECT_ITEM_TYPE } from '../../commons/constants';
import {
  ApartmentLayoutTypes,
  BuildingLayoutTypes,
  FloorLayoutTypes,
  VillaBlockLayoutTypes,
} from '../../types/layout';

export const getChildItems = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  { [key: string]: ProjectChildItemTypes },
  number,
  number,
  ProjectChildItemTypes[]
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.projectChildItemEntity.ids,
  (state) => state.sitePlanModel.projectItemId,
  (state) => state.sitePlanModel.floorId,
  (projectItemIds, floorIds, childItemIds, projectItemId, floorId) => {
    if (!projectItemId) return null;

    const projectItem = projectItemIds?.[projectItemId];
    if (!projectItem || !projectItem.itemIds) return null;

    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      if (!floorId) return null;

      const floor = floorIds?.[floorId];
      if (!floor || !floor.itemIds) return null;

      const items = [];
      [...floor.itemIds].forEach((id) => {
        const item = childItemIds?.[id];
        if (item) items.push(item);
      });

      return items;
    }

    if (projectItem.type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
      const items = [];

      [...projectItem.itemIds].forEach((id) => {
        const item = childItemIds?.[id];
        if (item) items.push(item);
      });

      return items;
    }

    return null;
  }
);

export const getChildItemById = createSelector<
  RootModel,
  { [key: string]: ProjectChildItemTypes },
  number,
  ProjectChildItemTypes
>(
  (state) => state.projectChildItemEntity.ids,
  (state) => state.sitePlanModel.projectChildItemId,
  (childItemIds, projectChildItemId) => childItemIds[projectChildItemId]
);

type ProjectChildItemLayoutConfigTypes = {
  layoutImage?: StoreImageTypes;
  layoutItems?: ApartmentLayoutTypes[] | VillaBlockLayoutTypes[];
  layoutConfig?: FloorLayoutTypes[] | BuildingLayoutTypes[];
  image?: StoreImageTypes;
  imageLayout?: StoreImageTypes;
};

export const getChildItemLayoutConfig = createSelector<
  RootModel,
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  { [key: string]: ProjectChildItemTypes },
  number,
  number,
  ProjectChildItemLayoutConfigTypes
>(
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.projectChildItemEntity.ids,
  (state) => state.sitePlanModel.projectItemId,
  (state) => state.sitePlanModel.floorId,
  (projectItemIds, floorIds, childItemIds, projectItemId, floorId) => {
    const projectItem = projectItemIds[projectItemId];
    if (!projectItem || projectItem.isSimplified) return {};

    let layoutConfig: FloorLayoutTypes[] | BuildingLayoutTypes[];
    let layoutImage: StoreImageTypes;
    let image: StoreImageTypes;
    let imageLayout: StoreImageTypes;
    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      const floor = floorIds[floorId];
      if (!floor || !floor.layoutItems) return {};

      layoutConfig = floor.layoutItems;
      layoutImage = floor.layoutImage;
      image = floor?.overlayLayout?.image;
      imageLayout = floor?.overlayLayout?.imageLayout;
    } else if (projectItem.type === PROJECT_ITEM_TYPE.VILLA_BLOCK) {
      layoutConfig = projectItem.layoutItems;
      layoutImage = projectItem.layoutImage;
      image = projectItem?.overlayLayout?.image;
      imageLayout = projectItem?.overlayLayout?.imageLayout;
    }

    if (!layoutConfig) return {};

    const layoutItems = layoutConfig.map((layoutItem: any) => ({
      ...layoutItem,
      item: childItemIds[layoutItem.projectChildItemId],
    }));

    return { layoutImage, layoutItems, image, imageLayout };
  }
);

export const getHoverChildItemById = createSelector<
  RootModel,
  { [key: string]: ProjectChildItemTypes },
  number,
  ProjectChildItemTypes
>(
  (state) => state.projectChildItemEntity.ids,
  (state) => state.sitePlanModel.hoverProjectChildItemId,
  (childItemIds, projectChildItemId) => childItemIds[projectChildItemId]
);

export const getProjectChildItemSimilar = createSelector<
  RootModel,
  { [key: string]: ProjectChildItemTypes },
  number,
  ProjectChildItemTypes[]
>(
  (state) => state.projectChildItemEntity.ids,
  (state) => state.homeProfileModel.homeId,
  (childItemIds, homeId) => {
    const projectChildItem = childItemIds[homeId];
    if (!projectChildItem) return;
    const projectChildItemsSimilar: ProjectChildItemTypes[] = [];
    const { similarItem } = projectChildItem;

    if (similarItem && similarItem.ids) {
      similarItem.ids.forEach((similarId) => {
        const item = childItemIds[similarId];
        if (item) {
          projectChildItemsSimilar.push(item);
        }
      });
    }

    return projectChildItemsSimilar;
  }
);
